import { sanitize } from "dompurify";
import { InsightsQueryData } from "./insights/InsightsWidgetSelectionDialog";

function formatCell(
  rowData: any,
  cellIdx: number,
  dataFormat: any[],
  column?: any
): JSX.Element {
  const formatIndex =
    cellIdx < dataFormat.length ? cellIdx : dataFormat.length - 1;
  const { decimalFormat, color, symbol, dateFormat } =
    dataFormat[formatIndex] || {};
  const cellValue = rowData.data;
  const isValidNumber = !isNaN(Number(cellValue));
  const isDate =
    typeof cellValue === "string" && cellValue.match(/^\d{4}-\d{2}-\d{2}T/);

  let formattedValue = cellValue;
  // Only apply numeric formatting if column.type is not 'Text', 'text', or 'string'
  if (isDate && dateFormat) {
    const date = new Date(cellValue);
    switch (dateFormat) {
      case "Years":
        formattedValue = date.getFullYear().toString();
        break;
      case "Months":
        formattedValue = `${(date.getMonth() + 1)
          .toString()
          .padStart(2, "0")}/${date.getFullYear()}`;
        break;
      case "Days":
        formattedValue = `${date.getMonth() + 1}/${date.getDate()}/${date
          .getFullYear()
          .toString()
          .slice(-2)}`;
        break;
      default:
        formattedValue = cellValue;
    }
  } else if (
    isValidNumber &&
    (symbol === "$" ||
      symbol === "%" ||
      !column ||
      (column.type !== "string" && column.type !== "text"))
  ) {
    let value = Number(cellValue);
    if (symbol === "%") {
      value *= 100;
    }
    if (isNaN(Number(decimalFormat)) || decimalFormat === "auto") {
      if (value < 1 && value > 0) {
        formattedValue = value.toLocaleString();
      } else if (value.toString().split(".")[1]?.length > 1) {
        formattedValue = value.toFixed(2);
      } else {
        formattedValue = Math.round(value).toString();
      }
    } else if (decimalFormat === 0) {
      formattedValue = Math.round(value).toString();
    } else {
      const format = Number(decimalFormat);
      formattedValue = value.toFixed(format);
    }
    formattedValue = Number(formattedValue).toLocaleString();
  }

  let formattedCellValue;
  // If dataFormat specifies a symbol ($ or %), apply it to valid numbers
  // The symbol in dataFormat is authoritative - it means this column IS currency/percent
  if (symbol === "$" && isValidNumber) {
    formattedCellValue = `${symbol}${formattedValue}`;
  } else if (symbol === "%" && isValidNumber) {
    formattedCellValue = `${formattedValue}${symbol}`;
  } else {
    formattedCellValue = formattedValue;
  }

  let backgroundColor = "inherit";
  if (Array.isArray(color)) {
    for (const condition of color) {
      const { operator, expression, color: conditionalColor } = condition;
      const expressionValue = Number(expression);
      const cellNumericValue = Number(cellValue);
      if (
        !isNaN(cellNumericValue) &&
        !isNaN(expressionValue) &&
        ((operator === "<" && cellNumericValue < expressionValue) ||
          (operator === "≤" && cellNumericValue <= expressionValue) ||
          (operator === ">" && cellNumericValue > expressionValue) ||
          (operator === "≥" && cellNumericValue >= expressionValue) ||
          (operator === "==" && cellNumericValue === expressionValue) ||
          (operator === "≠" && cellNumericValue !== expressionValue))
      ) {
        backgroundColor = conditionalColor;
        break;
      }
    }
  }

  if (typeof rowData.data === "string" && rowData.data.startsWith("<a href=")) {
    return (
      <td
        key={cellIdx}
        style={{
          color: Array.isArray(color) ? "inherit" : color || "inherit",
          backgroundColor,
        }}
        dangerouslySetInnerHTML={{ __html: sanitize(rowData.data) }}
      />
    );
  } else {
    return (
      <td
        key={cellIdx}
        style={{
          color: Array.isArray(color) ? "inherit" : color || "inherit",
          backgroundColor,
        }}
      >
        {formattedCellValue}
      </td>
    );
  }
}

function getFieldNames(
  queryData: InsightsQueryData,
  field: "values" | "columns" | "rows"
): string[] {
  if (field === "values" && Array.isArray((queryData as any).values)) {
    return ((queryData as any).values as { name: string }[]).map(
      (f: { name: string }) => f.name
    );
  }
  if (field === "columns" && Array.isArray(queryData.columns)) {
    return queryData.columns.map((f: { name: string }) => f.name);
  }
  if (field === "rows" && Array.isArray(queryData.rows)) {
    return [];
  }
  return [];
}

function formatHeaderValue(value: string, dateFormat?: string): string {
  if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
    const date = new Date(value);
    switch (dateFormat) {
      case "Years":
        return date.getFullYear().toString();
      case "Months":
        return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
          .getFullYear()
          .toString()}`;
      case "Days":
        return `${date.getMonth() + 1}/${date.getDate()}/${date
          .getFullYear()
          .toString()
          .slice(-2)}`;
      default:
        // Default to MM/DD/YY
        return `${date.getMonth() + 1}/${date.getDate()}/${date
          .getFullYear()
          .toString()
          .slice(-2)}`;
    }
  }
  // Format numbers (e.g. 12345.6789 => 12,345.68)
  if (!isNaN(Number(value)) && value.trim() !== "") {
    const num = Number(value);
    // Show up to 2 decimals if needed
    return num % 1 === 0
      ? num.toLocaleString()
      : num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return value;
}

function renderGroupedHeaderRow(
  level: string[],
  levelIdx: number,
  dataFormat: any[],
  startIdx = 0
) {
  const ths = [];
  let last: string | null = null;
  let colSpan = 0;
  let lastRawValue: string | null = null;
  for (let i = 0; i < level.length; i++) {
    const rawValue = level[i];
    const columnFormat = dataFormat[startIdx + i] || {};
    const displayValue = formatHeaderValue(rawValue, columnFormat.dateFormat);
    if (rawValue === lastRawValue) {
      colSpan++;
    } else {
      if (lastRawValue !== null && lastRawValue !== "") {
        ths.push(
          <th key={lastRawValue + i + "-" + levelIdx} colSpan={colSpan}>
            {last}
          </th>
        );
      }
      lastRawValue = rawValue;
      last = displayValue;
      colSpan = 1;
    }
  }
  if (lastRawValue !== null && lastRawValue !== "") {
    ths.push(
      <th key={lastRawValue + "-final-" + levelIdx} colSpan={colSpan}>
        {last}
      </th>
    );
  }
  return ths;
}

export function renderPivotTable(
  queryData: InsightsQueryData,
  showTitle: boolean
) {
  const { rows, widgetTitle, dataFormat, columns } = queryData;
  const valueNames: string[] = getFieldNames(queryData, "values");
  const columnNames: string[] = getFieldNames(queryData, "columns");
  // Multi-level header logic
  const splitColumns: string[][] = columnNames
    .slice(1)
    .map((col: string) => col.split(" | ").map((s: string) => s.trim()));
  const maxLevels: number = Math.max(
    ...splitColumns.map((parts: string[]) => parts.length)
  );
  const headerLevels: string[][] = Array.from(
    { length: maxLevels },
    (_, levelIdx: number) =>
      splitColumns.map((parts: string[]) =>
        parts[levelIdx] !== undefined ? parts[levelIdx] : ""
      )
  );

  return (
    <div>
      {showTitle && <h3>{widgetTitle || "No title"}</h3>}
      <table>
        <thead>
          <tr>
            <th rowSpan={headerLevels.length + 1}>{columnNames[0]}</th>
            {valueNames.length > 0 && (
              <th colSpan={columnNames.length - 1}>{valueNames.join(", ")}</th>
            )}
          </tr>
          {headerLevels.map((level: string[], levelIdx: number) => (
            <tr key={"header-level-" + levelIdx}>
              {renderGroupedHeaderRow(level, levelIdx, dataFormat, 1)}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.map((row: any[], rowIdx: number) => (
            <tr key={rowIdx}>
              {row.map((cell: any, idx: number) =>
                formatCell(
                  cell,
                  idx,
                  dataFormat,
                  columns ? columns[idx] : undefined
                )
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function renderValueColTable(
  queryData: InsightsQueryData,
  showTitle: boolean
) {
  const { rows, widgetTitle, dataFormat, columns } = queryData;
  const valueNames: string[] = getFieldNames(queryData, "values");
  const columnNames: string[] = getFieldNames(queryData, "columns");
  // Multi-level header logic
  const splitColumns: string[][] = columnNames.map((col: string) =>
    col.split(" | ").map((s: string) => s.trim())
  );
  const maxLevels: number = Math.max(
    ...splitColumns.map((parts: string[]) => parts.length)
  );
  const headerLevels: string[][] = Array.from(
    { length: maxLevels },
    (_, levelIdx: number) =>
      splitColumns.map((parts: string[]) =>
        parts[levelIdx] !== undefined ? parts[levelIdx] : ""
      )
  );

  return (
    <div>
      {showTitle && <h3>{widgetTitle || "No title"}</h3>}
      <table>
        <thead>
          {valueNames.length > 0 && (
            <tr>
              <th colSpan={columnNames.length}>{valueNames.join(", ")}</th>
            </tr>
          )}
          {headerLevels.map((level: string[], levelIdx: number) => (
            <tr key={"header-level-" + levelIdx}>
              {renderGroupedHeaderRow(level, levelIdx, dataFormat, 0)}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.map((row: any[], rowIdx: number) => (
            <tr key={rowIdx}>
              {row.map((cell: any, idx: number) =>
                formatCell(
                  cell,
                  idx,
                  dataFormat,
                  columns ? columns[idx] : undefined
                )
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function renderSingleLevelTable(
  queryData: InsightsQueryData,
  showTitle: boolean
) {
  const { columns, rows, widgetTitle, dataFormat } = queryData;
  const thElements = columns.map((col: { name: string }, idx: number) => (
    <th key={idx}>{col.name}</th>
  ));
  const trElements = rows.map((row: any[], rowIdx: number) => (
    <tr key={rowIdx}>
      {row.map((rowData: any, cellIdx: number) =>
        formatCell(rowData, cellIdx, dataFormat, columns[cellIdx])
      )}
    </tr>
  ));
  return (
    <div>
      {showTitle && <h3>{widgetTitle || "No title"}</h3>}
      {rows.length ? (
        <table>
          <thead>
            <tr>{thElements}</tr>
          </thead>
          <tbody>{trElements}</tbody>
        </table>
      ) : (
        <div>No data for selected filters</div>
      )}
    </div>
  );
}
